import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, FileSearch, Lightbulb, Zap, TrendingUp, Shield, Target, Settings, Users, BarChart3 } from 'lucide-react';

const OurApproach = () => {
  const phases = [
    {
      icon: Search,
      number: '1',
      title: 'Discover & Clarify',
      tagline: 'Ambition | Risks | Success Criteria',
      details: 'We work with leadership to clarify strategic ambition, execution risks, and success criteria - anchoring people decisions to business intent from the outset.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: FileSearch,
      number: '2',
      title: 'Diagnose',
      tagline: 'Leadership | Culture | Systems | People Practices',
      details: 'We assess leadership depth, cultural dynamics, systems, and people practices to identify what enables progress - and what is likely to constrain scale next.',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: Lightbulb,
      number: '3',
      title: 'Design',
      tagline: 'Sequenced Roadmap | ROI Logic',
      details: 'We define a prioritised, sequenced roadmap across structure, governance, leadership, culture, and the HR operating model - grounded in adoption, impact, and return on effort.',
      color: 'from-amber-500 to-amber-600'
    },
    {
      icon: Zap,
      number: '4',
      title: 'Pilot & Execute',
      tagline: 'Client-Led | Specialist-Supported',
      details: 'We move decisively into execution - either governing client-led delivery or deploying specialised consultants to implement defined transformation modules.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: TrendingUp,
      number: '5',
      title: 'Scale',
      tagline: 'Enterprise Rollout | Governance',
      details: 'We support enterprise-wide rollout through clear governance, change discipline, and performance tracking - ensuring consistency, pace, and control.',
      color: 'from-rose-500 to-rose-600'
    },
    {
      icon: Shield,
      number: '6',
      title: 'Sustain',
      tagline: 'Reviews | Coaching | Optional Outsourcing',
      details: 'We remain engaged through structured reviews, leadership coaching, and optional HR outsourcing - reinforcing progress and embedding new capabilities over time.',
      color: 'from-cyan-500 to-cyan-600'
    }
  ];

  const whyItWorks = [
    {
      icon: Target,
      title: 'Context shapes the solution',
      description: 'Every engagement is grounded in organisational reality - stage, market, ownership, and ambition. Design follows context, not templates.'
    },
    {
      icon: Users,
      title: 'Ownership is embedded',
      description: 'Transformation is built with leadership, not handed over to them - driving adoption, accountability, and sustained change.'
    },
    {
      icon: BarChart3,
      title: 'Outcomes are explicit',
      description: 'Each phase is tied to clear business outcomes and measurable impact. Effort is directed where it matters most.'
    },
    {
      icon: Settings,
      title: 'Discipline over haste',
      description: 'Progress follows a sequenced roadmap - moving with intent, balancing speed with rigour, and execution with control.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=2070&q=80"
            alt="Strategic approach"
            className="w-full h-full object-cover opacity-15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
              Our Approach
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                From insight to execution
              </span>
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl font-light mb-12 text-slate-300">
              A structured, context-led path to organisational readiness.
            </p>
          </div>
        </div>
      </section>

      {/* Our Execution Framework */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Our Execution Framework
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              A structured, six-phase methodology designed to build organisational readiness and sustain execution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {phases.map((phase, index) => {
              const Icon = phase.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-slate-50 border border-slate-200 rounded-xl hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${phase.color}`}></div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-r ${phase.color}`}>
                      <span className="text-xl font-bold text-white">{phase.number}</span>
                    </div>
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-r ${phase.color}`}>
                      <Icon className="text-white" size={28} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{phase.title}</h3>
                  <p className="text-amber-600 text-sm font-semibold mb-4">{phase.tagline}</p>
                  <p className="text-slate-600 leading-relaxed">{phase.details}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <p className="text-slate-600 text-lg mb-2">Progress is not rushed...</p>
            <p className="font-semibold text-xl text-amber-600">
              It is deliberately built - and designed to last.
            </p>
          </div>
        </div>
      </section>

      {/* Why This Approach Works */}
      <section className="py-20 lg:py-28 bg-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Why This Approach Works
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyItWorks.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="p-8 bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-amber-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              Building clarity before scaling further?
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              Let's discuss how a structured, context-led approach supports execution.
            </p>
            <Link
              to="/contact#ready-to-move"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg group">
              Let's Connect
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurApproach;
